import React, { Fragment, useEffect, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import get from 'lodash/get';

import {
  FIELD_FILLING_TYPES,
  getFieldProps,
} from '../../../../utils/fields/getFieldProps';
import { PROFILE_FIELDS_TYPES } from '../../../../constants/formFields';
import { FormErrorBlock } from '../../../common/FormErrorBlock';
import SelectCustom from '../../../SelectCustom';
import TextInput from '../../../TextInput';
import { useLoggedUserCountry } from '../../../DependentForm/utils/useLoggedUserCountry';
import {
  adaptInitialValueCountry,
  adaptInitialValueState,
} from '../../../ProfileLayout/HomeAddress/utils';
import ErrorBlock from '../../../common/ErrorBlock';

const {
  COUNTRY,
  POSTAL_CODE,
  STATE,
  CITY,
  ADDRESS_1,
  ADDRESS_2,
} = PROFILE_FIELDS_TYPES;

const PatientAddressForm = ({
  city,
  state,
  country,
  onChange,
  address1,
  address2,
  countries,
  statesList,
  postalCode,
  validateErrors,
  formatMessage,
  zipLimit,
  onCountryChange,
  isZipApplicable,
  zipExamples,
  loadCountryDetails,
}) => {
  const makeProfileField = getFieldProps(formatMessage, onChange);
  const targetCountryEntity = useLoggedUserCountry();

  const handleCountryChange = useMemo(
    () => data => {
      if (!data) return;
      if (country === data || country === data.label) {
        onCountryChange(data);
        return;
      }
      onCountryChange(data);
    },
    [country, targetCountryEntity, city, address1, address2],
  );

  const safeCountry = adaptInitialValueCountry(country, countries);
  const safeState = adaptInitialValueState(state, statesList);

  const upperCasedPostalCode = postalCode
    ? postalCode.toUpperCase()
    : postalCode;

  useEffect(() => {
    if (!country && targetCountryEntity)
      loadCountryDetails(targetCountryEntity.value);
    onChange({
      [PROFILE_FIELDS_TYPES.COUNTRY]: targetCountryEntity,
    });
    if (country) {
      const countryEntity = adaptInitialValueCountry(
        country.label || country,
        countries,
      );
      loadCountryDetails(countryEntity.value);
      onChange({
        [PROFILE_FIELDS_TYPES.STATE]: safeState,
        [PROFILE_FIELDS_TYPES.COUNTRY]: safeCountry,
      });
    }
  }, [targetCountryEntity, country, safeState]);

  return (
    <Fragment>
      {/*     C O U N T R Y     */}
      <SelectCustom
        {...makeProfileField(COUNTRY, FIELD_FILLING_TYPES.REQUIRED)}
        options={countries}
        value={safeCountry || targetCountryEntity}
        needUpdateDefaultValue
        onChange={val => handleCountryChange(val)}
      />
      <FormErrorBlock type={COUNTRY} errors={validateErrors} />
      {/*     P O S T A L   C O D E     */}
      <TextInput
        {...makeProfileField(POSTAL_CODE, FIELD_FILLING_TYPES.REQUIRED)}
        limit={zipLimit}
        disabled={!isZipApplicable}
        value={upperCasedPostalCode}
      />
      <If condition={get(validateErrors, POSTAL_CODE)}>
        <ErrorBlock
          errors={[
            formatMessage(
              { id: 'errors.validation.profile.postalCode.format' },
              { example: zipExamples },
            ),
          ]}
          className="mt3 w-100"
        />
      </If>
      {/*     S T A T E     */}
      <SelectCustom
        {...makeProfileField(STATE, FIELD_FILLING_TYPES.REQUIRED)}
        onChange={data => onChange({ state: data })}
        value={safeState}
        options={statesList}
        needUpdateDefaultValue
      />
      <FormErrorBlock type={STATE} errors={validateErrors} />
      {/*     C I T Y     */}
      <TextInput
        {...makeProfileField(CITY, FIELD_FILLING_TYPES.REQUIRED)}
        value={city}
      />
      <FormErrorBlock type={CITY} errors={validateErrors} />
      {/*     A D D R E S S  1     */}
      <TextInput {...makeProfileField(ADDRESS_1)} value={address1} />
      <FormErrorBlock type={ADDRESS_1} errors={validateErrors} />
      {/*     A D D R E S S  2     */}
      <TextInput {...makeProfileField(ADDRESS_2)} value={address2} />
      <FormErrorBlock type={ADDRESS_2} errors={validateErrors} />
    </Fragment>
  );
};

PatientAddressForm.propTypes = {
  validateErrors: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({ description: PropTypes.string }),
  ]),
  formatMessage: PropTypes.func,
  onChange: PropTypes.func,
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  state: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  postalCode: PropTypes.string,
  countries: PropTypes.array,
  statesList: PropTypes.array,
  zipLimit: PropTypes.number,
  onCountryChange: PropTypes.func,
  isZipApplicable: PropTypes.bool,
  zipExamples: PropTypes.string,
  loadCountryDetails: PropTypes.func,
};

export default PatientAddressForm;



// WEBPACK FOOTER //
// ./src/components/PatientRegistration/PatientRegistrationAddress/PatientAddressForm/index.jsx